/**
 * This component wraps React-Router's Link by providing name-based routing.
 *
 * The `name` prop should match a route in the flattened
 * routeConfiguration passed in context by the RoutesProvider
 * component. The `params` props is the route params for the route
 * path of the given route name.
 *
 * The `to` prop is an object with the same shape as Link requires,
 * but without `pathname` that will be generated from the given route
 * name.
 *
 * Some additional props can be passed for the <a> element like
 * `className` and `style`.
 *
 * The component can also be given the `activeClassName` prop that
 * will be added to the element className if the current URL matches
 * the one in the generated pathname of the link.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Link, withRouter } from 'react-router-dom';
import classNames from 'classnames';
import routeConfiguration from '../../routeConfiguration';
import { isMedian } from '../../util/median';
import { setMedianBackFriendlyNavigationTitle } from '../../util/medianBridge';
import { pathByRouteName } from '../../util/routes';

const canSetBackFriendlyTitle = (event) => {
  return (
    !event.defaultPrevented &&
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey
  );
};

const isCurrentLocationHref = (href) => {
  if (typeof window === 'undefined') {
    return false;
  }

  const currentHref = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  return currentHref === href;
};

export const NamedLinkComponent = (props) => {
  const { name, params, title } = props;

  // Link props
  const { to, children } = props;
  const pathname = pathByRouteName(name, routeConfiguration(), params);
  const { match } = props;
  const active = match.url && match.url === pathname;

  const rawSearch = to?.search || '';
  const search = rawSearch && rawSearch.charAt(0) !== '?' ? `?${rawSearch}` : rawSearch;
  const hash = to?.hash || '';
  const href = `${pathname}${search}${hash}`;
  const shouldUseHardNavigation = isMedian() && !to?.state;

  const handleMedianClick = (event) => {
    if (!canSetBackFriendlyTitle(event) || isCurrentLocationHref(href)) {
      return;
    }

    setMedianBackFriendlyNavigationTitle().catch(() => null);
  };

  // <a> element props
  const { className, style, activeClassName } = props;
  const aElemProps = {
    className: classNames(className, { [activeClassName]: active }),
    style,
    title,
  };

  if (shouldUseHardNavigation) {
    return (
      <a href={href} onClick={handleMedianClick} {...aElemProps}>
        {children}
      </a>
    );
  }

  return (
    <Link to={{ pathname, ...to }} {...aElemProps}>
      {children}
    </Link>
  );
};

const { object, string, shape, any } = PropTypes;

NamedLinkComponent.defaultProps = {
  params: {},
  to: {},
  children: null,
  className: '',
  style: {},
  activeClassName: 'NamedLink_active',
  title: null,
  match: {},
};

// This ensures a nice display name in snapshots etc.
NamedLinkComponent.displayName = 'NamedLink';

NamedLinkComponent.propTypes = {
  // name of the route in routeConfiguration
  name: string.isRequired,
  // params object for the named route
  params: object,
  // Link component props
  to: shape({ search: string, hash: string, state: object }),
  children: any,

  // generic props for the underlying <a> element
  className: string,
  style: object,
  activeClassName: string,
  title: string,

  // from withRouter
  match: object,
};

const NamedLink = withRouter(NamedLinkComponent);
NamedLink.displayName = 'NamedLink';

export default NamedLink;
